import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card } from '../../components/ui/Kit';
import CopyButton from '../../components/ui/CopyButton';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import { api } from '../../lib/api';

const {
  FiExternalLink,
  FiLink,
  FiList,
  FiBriefcase,
  FiMapPin,
  FiBookOpen,
  FiCalendar,
  FiFileText,
  FiUsers,
  FiShield,
  FiEdit,
  FiAward,
  FiActivity,
} = FiIcons;

const SUB_VIEWS = [
  { id: 'licensing', label: 'Licensing' },
  { id: 'links', label: 'Resource Links' },
  { id: 'providers', label: 'Product Providers' },
];

/** Phase labels / icons keyed by the `phase` column. */
const LICENSING_PHASES = [
  { key: 'education', label: 'Education', icon: FiBookOpen },
  { key: 'exam', label: 'Exam', icon: FiCalendar },
  { key: 'fingerprinting', label: 'Fingerprinting', icon: FiActivity },
  { key: 'application', label: 'Application', icon: FiFileText },
  { key: 'contracting', label: 'Contracting', icon: FiUsers },
  { key: 'manage_license', label: 'Manage Your License', icon: FiAward },
  { key: 'compliance', label: 'Compliance & CE', icon: FiShield },
];

const PROVIDER_COLORS = [
  'bg-[#00AAFF]',
  'bg-[#1B4C87]',
  'bg-teal-600',
  'bg-indigo-600',
  'bg-violet-600',
  'bg-cyan-600',
  'bg-sky-600',
  'bg-blue-700',
];

function normalizeLinks(links) {
  if (!Array.isArray(links)) return [];
  return links
    .map((l) => ({
      label: typeof l?.label === 'string' ? l.label.trim() : '',
      url: typeof l?.url === 'string' ? l.url.trim() : '',
    }))
    .filter((l) => l.label || l.url);
}

function userLicensingStates(user) {
  const raw = user?.licensing_state;
  if (!Array.isArray(raw)) return [];
  return raw.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean);
}

function providerColor(title) {
  const str = String(title || '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return PROVIDER_COLORS[hash % PROVIDER_COLORS.length];
}

function linkIconForTitle(title) {
  const t = String(title || '').toLowerCase();
  if (t.includes('write') || t.includes('post')) return FiEdit;
  if (t.includes('blog') || t.includes('article') || t.includes('guide')) return FiFileText;
  if (t.includes('portal') || t.includes('login')) return FiExternalLink;
  return FiLink;
}

function OpenLinkButton({ href, label = 'Open link', compact = false }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 rounded-md bg-[#00AAFF] font-medium text-white shadow-sm hover:bg-[#0099e6] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF] focus-visible:ring-offset-1 ${
        compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-xs sm:text-sm'
      }`}
    >
      {label}
      <SafeIcon icon={FiExternalLink} className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
    </a>
  );
}

function LinkActions({ href, openLabel = 'Open link', compact = false }) {
  if (!href) return null;
  return (
    <div className="inline-flex items-center gap-1.5">
      <OpenLinkButton href={href} label={openLabel} compact={compact} />
      <CopyButton
        text={href}
        title="Copy link"
        className="w-8 h-8 rounded-md text-gray-400 hover:text-[#00AAFF] hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF]"
      />
    </div>
  );
}

function StateBadges({ states }) {
  const list = Array.isArray(states) ? states : [];
  if (!list.length || list.includes('ALL')) {
    return <Badge variant="default">All States</Badge>;
  }
  return (
    <>
      {list.map((s) => (
        <Badge key={s} variant="default">
          {s}
        </Badge>
      ))}
    </>
  );
}

function SubNav({ active, onChange }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1">
      {SUB_VIEWS.map((view) => (
        <button
          key={view.id}
          type="button"
          onClick={() => onChange(view.id)}
          className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF] ${
            active === view.id
              ? 'bg-white text-[#00AAFF] shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {view.label}
        </button>
      ))}
    </div>
  );
}

function LicensingView({ steps, userStates, hasProfileStates }) {
  const availableStates = useMemo(() => {
    const set = new Set();
    for (const row of steps) {
      for (const s of row.applies_to_states || []) {
        const code = String(s || '').trim().toUpperCase();
        if (code && code !== 'ALL') set.add(code);
      }
    }
    return [...set].sort();
  }, [steps]);

  const defaultState = useMemo(() => {
    if (!userStates.length) return null;
    const match = userStates.find((s) => availableStates.includes(s));
    return match || userStates[0] || null;
  }, [userStates, availableStates]);

  const [showAllStates, setShowAllStates] = useState(!hasProfileStates);
  const [selectedState, setSelectedState] = useState(defaultState);

  useEffect(() => {
    if (!hasProfileStates) {
      setShowAllStates(true);
      return;
    }
    setShowAllStates(false);
    setSelectedState(defaultState);
  }, [hasProfileStates, defaultState]);

  const phases = useMemo(() => {
    const rowStates = (row) =>
      (row.applies_to_states || [])
        .map((s) => String(s || '').trim().toUpperCase())
        .filter(Boolean);

    const isGenericAll = (row) => {
      const states = rowStates(row);
      return states.length > 0 && states.every((s) => s === 'ALL');
    };

    return LICENSING_PHASES.map((meta) => {
      const phaseRows = steps.filter((row) => row.phase === meta.key);
      let rows;

      if (showAllStates || !selectedState) {
        // Generic overview: ALL-tagged fallback rows only
        rows = phaseRows.filter(isGenericAll);
      } else {
        const specific = phaseRows.filter((row) => rowStates(row).includes(selectedState));
        if (specific.length > 0) {
          // State-specific row(s) supersede generic ALL for this phase
          rows = specific;
        } else {
          rows = phaseRows.filter(isGenericAll);
        }
      }

      rows = [...rows].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));
      return { ...meta, rows };
    }).filter((phase) => phase.rows.length > 0);
  }, [steps, showAllStates, selectedState]);

  const filteredCount = useMemo(
    () => phases.reduce((sum, phase) => sum + phase.rows.length, 0),
    [phases],
  );

  if (steps.length === 0) {
    return (
      <EmptyState
        icon={FiList}
        title="No licensing steps yet"
        description="Your admin will add licensing checklist steps here soon."
      />
    );
  }

  return (
    <div className="space-y-5">
      {!hasProfileStates && (
        <div className="rounded-lg border border-[#00AAFF]/25 bg-[#00AAFF]/5 px-3.5 py-2.5 text-sm text-[#1B4C87]">
          Tip: set your licensed state(s) in{' '}
          <Link
            to="/profile"
            className="font-semibold text-[#00AAFF] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF] rounded-sm"
          >
            Profile
          </Link>{' '}
          so we can highlight the right steps. Showing general guidance for now.
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2.5">
          <div className="flex items-center gap-1.5 text-sm text-gray-600">
            <SafeIcon icon={FiMapPin} className="w-4 h-4 text-[#00AAFF]" />
            <span className="font-medium">State</span>
          </div>
          <div className="inline-flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setShowAllStates(true)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF] ${
                showAllStates
                  ? 'bg-[#00AAFF] text-white border-[#00AAFF]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#00AAFF]/50'
              }`}
            >
              Show All States
            </button>
            {availableStates.map((code) => {
              const active = !showAllStates && selectedState === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    setShowAllStates(false);
                    setSelectedState(code);
                  }}
                  className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00AAFF] ${
                    active
                      ? 'bg-[#00AAFF] text-white border-[#00AAFF]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#00AAFF]/50'
                  }`}
                >
                  {code}
                </button>
              );
            })}
          </div>
        </div>
        {showAllStates && (
          <p className="text-xs text-gray-500 sm:pl-6">
            Showing general guidance. Select your state above for state-specific instructions and
            codes.
          </p>
        )}
      </div>

      {filteredCount === 0 ? (
        <EmptyState
          icon={FiList}
          title={showAllStates ? 'No general guidance yet' : 'No steps for this state'}
          description={
            showAllStates
              ? 'Select a state above, or ask an admin to add All States fallback steps.'
              : 'Try another state or show all states for general guidance.'
          }
        />
      ) : (
        <div className="space-y-6">
          {phases.map((phase) => (
            <section key={phase.key} className="space-y-2.5">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00AAFF]/10 text-[#00AAFF]">
                  <SafeIcon icon={phase.icon} className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-[#1B4C87] tracking-wide uppercase">
                    {phase.label}
                  </h3>
                </div>
              </div>
              <div className="space-y-2 pl-0 sm:pl-2 border-l-0 sm:border-l-2 sm:border-[#00AAFF]/20 sm:ml-4 sm:pl-4">
                {phase.rows.map((row) => (
                  <Card
                    key={row.id}
                    className="border border-gray-200 shadow-none hover:border-[#00AAFF]/30 transition-colors"
                  >
                    <div className="px-3.5 py-3 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-semibold text-gray-900">{row.title}</h4>
                        <StateBadges states={row.applies_to_states} />
                      </div>
                      {row.instructions ? (
                        <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                          {row.instructions}
                        </p>
                      ) : null}
                      {row.url ? <LinkActions href={row.url} compact /> : null}
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceLinksView({ rows }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={FiLink}
        title="No resource links yet"
        description="Your admin will add helpful portals and documents here soon."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map((row) => {
        const icon = linkIconForTitle(row.title);
        return (
          <Card
            key={row.id}
            className="flex flex-col border border-gray-200 shadow-sm hover:border-[#00AAFF]/35 transition-colors"
          >
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00AAFF]/10 text-[#00AAFF]">
                  <SafeIcon icon={icon} className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 leading-snug">{row.title}</p>
                  {row.instructions ? (
                    <p className="text-xs text-gray-500 mt-1 whitespace-pre-line line-clamp-2">
                      {row.instructions}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="mt-auto pt-1">
                {row.url ? (
                  <LinkActions href={row.url} />
                ) : (
                  <span className="text-xs text-gray-400">No link provided</span>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function ProvidersView({ rows }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="No product providers yet"
        description="Your admin will add carrier marketplace entries here soon."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {rows.map((row) => {
        const links = normalizeLinks(row.links);
        const initial = (row.title || '?').trim().charAt(0).toUpperCase() || '?';
        return (
          <Card
            key={row.id}
            className="flex flex-col border border-gray-200 shadow-sm hover:border-[#00AAFF]/35 transition-colors"
          >
            <div className="p-4 flex flex-col flex-1">
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${providerColor(row.title)}`}
                  aria-hidden
                >
                  {initial}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 leading-snug">{row.title}</p>
                  {row.subtitle ? (
                    <span className="inline-flex mt-1">
                      <Badge variant="primary">{row.subtitle}</Badge>
                    </span>
                  ) : null}
                </div>
              </div>

              {row.instructions ? (
                <p className="text-sm text-gray-600 mt-3 whitespace-pre-line leading-relaxed">
                  {row.instructions}
                </p>
              ) : null}

              <div className="mt-auto pt-3 space-y-1.5">
                {links.length > 0 ? (
                  links.map((link, i) => (
                    <div
                      key={`${row.id}-link-${i}`}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-2.5 py-2"
                    >
                      <span className="text-xs font-medium text-gray-700 min-w-0 truncate">
                        {link.label || link.url || 'Link'}
                      </span>
                      {link.url ? (
                        <LinkActions href={link.url} openLabel="Open" compact />
                      ) : null}
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-gray-400">No links yet</span>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

export default function AgentResourcesTab() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const agencyId = user?.agency_id;
  const userStates = useMemo(() => userLicensingStates(user), [user]);
  const hasProfileStates = userStates.length > 0;

  const [subView, setSubView] = useState('licensing');
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);

  const load = useCallback(async () => {
    if (!agencyId) {
      setResources([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await api.licensingResources.getAll(agencyId);
      setResources((rows || []).filter((r) => r.is_active !== false));
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load agent resources',
        message: err.message,
      });
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [agencyId, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const steps = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'licensing_step')
        .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0)),
    [resources],
  );

  const generalLinks = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'general_link')
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [resources],
  );

  const providers = useMemo(
    () =>
      resources
        .filter((r) => r.category === 'provider')
        .sort((a, b) => (a.title || '').localeCompare(b.title || '')),
    [resources],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
        <Spinner className="text-[#00AAFF]" />
        Loading agent resources…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <SubNav active={subView} onChange={setSubView} />

      {subView === 'licensing' && (
        <LicensingView
          steps={steps}
          userStates={userStates}
          hasProfileStates={hasProfileStates}
        />
      )}
      {subView === 'links' && <ResourceLinksView rows={generalLinks} />}
      {subView === 'providers' && <ProvidersView rows={providers} />}
    </div>
  );
}
